<template>
    <div>
        <!-- Dialog -->
        <el-dialog
            :visible="visible"
            :before-close="handleBeforeClose"
            :close-on-click-modal="false"
            width="55%"
        >
            <!-- Header -->
            <template slot="title">
                <h5 class="text-dark mb-0">📞 Toll-Free Verification</h5>
            </template>

            <!-- Content -->
            <el-form
                :model="tollFreeVerificationForm"
                :rules="formRules"
                v-loading="creating_toll_free_verification"
                label-position="top"
                ref="tollFreeVerificationForm"
            >
                <div class="row">
                    <!-- Status Alert -->
                    <div class="col-12">
                        <!-- Title Alert -->
                        <el-alert
                            v-if="verification && verification.status !== ComplianceBundleStatuses.STATUS_APPROVED"
                            show-icon
                            :type="statusAlertType"
                            :closable="false"
                        >
                            <template slot="title">
                                <span class="font-weight-bold">{{ statusAlertTitle }}</span>
                            </template>
                        </el-alert>

                        <!-- Success Alert for Approved Status -->
                        <el-alert
                            v-if="verification && verification.status === ComplianceBundleStatuses.STATUS_APPROVED"
                            show-icon
                            type="success"
                            :closable="false"
                        >
                            <template slot="title">
                                <span class="font-weight-bold">This toll-free verification has been approved.</span>
                            </template>
                            <p class="mb-0">Your toll-free number is now fully verified and ready for messaging use.</p>
                        </el-alert>

                        <!-- Details Alert for Rejected Status -->
                        <el-alert
                            v-if="verification && verification.status === ComplianceBundleStatuses.STATUS_REJECTED"
                            type="error"
                            :closable="false"
                            class="mb-3 text-black"
                        >
                            <!-- Rejection Reasons Array (structured format) -->
                            <div v-if="verification.failure_reasons && verification.failure_reasons.length" class="mb-3">
                                <strong class="text-black">Rejection Reasons:</strong>
                                <ul class="mb-0 mt-1">
                                    <li 
                                        v-for="(reason, index) in verification.failure_reasons" 
                                        :key="`reason-${index}`"
                                        class="text-black mb-1"
                                    >
                                        <strong>Code {{ reason.code }}:</strong> {{ reason.reason }}
                                    </li>
                                </ul>
                            </div>

                            <!-- Legacy Rejection Reason and Error Code (still valid for some errors) -->
                            <div v-else-if="verification.rejection_reason && verification.error_code" class="mb-3">
                                <strong class="text-black">Rejection Reasons:</strong>
                                <p class="mb-0 text-black">
                                    <strong>Error Code {{ verification.error_code }}:</strong> {{ verification.rejection_reason }}
                                </p>
                            </div>

                            <!-- Next Steps -->
                            <div>
                                <strong class="text-black">Next Steps:</strong>
                                <div v-if="verification.edit_allowed && !isEditExpired" class="mb-0 mt-1">
                                    <p class="text-black">
                                        Review the rejection details above, make the necessary adjustments, and resubmit the verification.
                                    </p>
                                    <p v-if="verification.edit_expiration" class="text-black">
                                        <strong>Note:</strong> You can resubmit until <strong>{{ verification.edit_expiration | fixFullDateUTC }}</strong>, after which you'll need to create a new verification.
                                    </p>
                                </div>
                                <div v-else-if="verification.edit_allowed && isEditExpired" class="mb-0 mt-1">
                                    <p class="text-black">
                                        <strong>The edit period has expired.</strong> You can no longer edit this verification as the deadline of <strong>{{ verification.edit_expiration | fixFullDateUTC }}</strong> has passed.
                                    </p>
                                    <p class="text-black">
                                        Please delete this verification and create a new one.
                                    </p>
                                </div>
                                <p v-else class="mb-0 mt-1 text-black">
                                    This verification cannot be edited. You need to delete it and submit a new verification request.
                                </p>
                            </div>
                        </el-alert>

                        <!-- Details Alert for Pending/In Review Status -->
                        <el-alert
                            v-if="verification && (verification.status === ComplianceBundleStatuses.STATUS_IN_REVIEW || verification.status === ComplianceBundleStatuses.STATUS_PENDING)"
                            type="info"
                            :closable="false"
                            class="mb-3 text-black"
                        >
                            <p class="mb-0">Your verification is currently being reviewed. This process typically takes 1-3 business days.</p>
                        </el-alert>
                    </div>

                    <div class="col-12">
                        <div class="row">
                            <!-- Business Information Section -->
                            <div class="col-12 mb-3">
                                <span class="text-dark h6">
                                    <i class="material-icons mr-2">business</i>Business Information
                                </span>
                            </div>

                            <div class="col-12 mt-1">
                                <!-- Customer Profile Selection -->
                                <el-form-item prop="customer_profile_id" label="Customer Profile">
                                    <span class="page-anchor" />
                                    <p class="line-height-normal">Select the approved customer profile to associate with this toll-free verification.</p>

                                    <el-select
                                        v-model="tollFreeVerificationForm.customer_profile_id"
                                        placeholder="Select Customer Profile"
                                        class="w-full"
                                        filterable
                                        @change="handleCustomerProfileChange"
                                        :disabled="readOnlyMode"
                                    >
                                        <el-option
                                            v-for="customer_profile in customerProfiles"
                                            :key="customer_profile.id"
                                            :value="customer_profile.id"
                                            :label="`#${customer_profile.id} - ${customer_profile.friendly_name} (${customer_profile.sid})`"
                                            :disabled="customer_profile.status !== ComplianceBundleStatuses.STATUS_APPROVED"
                                        >
                                            <span>#{{ customer_profile.id }} - {{ customer_profile.friendly_name }} ({{ customer_profile.sid }})</span>
                                            <small class="ml-2 text-dark font-weight-bold">
                                                <b
                                                    v-if="customer_profile.status"
                                                    class="label"
                                                    :class="customer_profile.status | statusColor"
                                                >
                                                    {{ customer_profile.status_name }}
                                                </b>
                                            </small>
                                        </el-option>
                                    </el-select>
                                </el-form-item>
                            </div>

                            <div class="col-6 mt-3">
                                <!-- Business Name -->
                                <el-form-item prop="business_name" label="Business Name">
                                    <span class="page-anchor" />
                                    <p class="line-height-normal">The legal business name for this toll-free verification.</p>

                                    <el-input
                                        v-model="tollFreeVerificationForm.business_name"
                                        placeholder="Enter business name..."
                                        maxlength="100"
                                        show-word-limit
                                        :disabled="isBasicFieldDisabled"
                                        @change="handleFormFieldChange"
                                    />
                                </el-form-item>
                            </div>

                            <div class="col-6 mt-3">
                                <!-- Website URL -->
                                <el-form-item prop="website_url" label="Website URL">
                                    <span class="page-anchor" />
                                    <p class="line-height-normal">The business website URL for verification purposes.</p>

                                    <el-input
                                        v-model="tollFreeVerificationForm.website_url"
                                        placeholder="https://example.com"
                                        maxlength="200"
                                        show-word-limit
                                        :disabled="isBasicFieldDisabled"
                                        @input="handleFormFieldChange"
                                        @blur="() => { website_url = website_url; handleFormFieldChange(); }"
                                    />
                                </el-form-item>
                            </div>

                            <!-- Customer Profile Information Display -->
                            <div v-if="customer_profile_info" class="col-12 mt-3">
                                <div class="row">
                                    <!-- Business Address -->
                                    <div class="col-6 mb-3" v-if="customer_profile_info.business_address">
                                        <p class="font-weight-bold">Business Address</p>
                                        <div class="mt-1 text-muted">
                                            <div>
                                                {{ customer_profile_info.business_address.street }}
                                                <span v-if="customer_profile_info.business_address.street_secondary">
                                                    , {{ customer_profile_info.business_address.street_secondary }}
                                                </span>
                                            </div>
                                            <div>
                                                {{ customer_profile_info.business_address.city }}
                                                <span>, </span>
                                                <span>{{ customer_profile_info.business_address.region }} {{ customer_profile_info.business_address.postal_code }}</span>
                                            </div>
                                            <div>
                                                {{ customer_profile_info.business_address.country }}
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Contact Information -->
                                    <div class="col-6 mb-3" v-if="customer_profile_info.contact_information">
                                        <p class="font-weight-bold">Contact Information</p>
                                        <div class="mt-1 text-muted">
                                            <div v-if="customer_profile_info.contact_information.name">
                                                {{ customer_profile_info.contact_information.name }}
                                            </div>
                                            <div v-if="customer_profile_info.contact_information.email">
                                                {{ customer_profile_info.contact_information.email }}
                                            </div>
                                            <div v-if="customer_profile_info.contact_information.phone">
                                                {{ customer_profile_info.contact_information.phone | fixPhone }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="col-12 mt-1">
                                <!-- Toll-Free Number Selection -->
                                <el-form-item prop="incoming_number_ids" label="Toll-Free Numbers">
                                    <span class="page-anchor" />
                                    <p class="line-height-normal">Select unverified toll-free numbers to associate with this verification. Already submitted numbers are disabled. Multiple numbers can be selected for initial submission.</p>

                                    <el-select
                                        v-model="tollFreeVerificationForm.incoming_number_ids"
                                        placeholder="Select Toll-Free Numbers"
                                        class="w-full"
                                        filterable
                                        multiple
                                        :disabled="isBasicFieldDisabled"
                                        :loading="loading_incoming_numbers"
                                        :multiple-limit="readOnlyMode ? 1 : 0"
                                        @change="handleFormFieldChange"
                                    >
                                        <el-option
                                            v-for="incoming_number in sortedIncomingNumbers"
                                            :key="incoming_number.id"
                                            :value="incoming_number.id"
                                            :label="getNumberLabel(incoming_number)"
                                            :disabled="isNumberVerified(incoming_number)"
                                        >
                                            <span>{{ getNumberLabel(incoming_number) }}</span>
                                            <small class="ml-2 text-dark font-weight-bold">
                                                <b
                                                    class="label"
                                                    :class="getNumberStatus(incoming_number) | statusColor"
                                                >
                                                    {{ getNumberStatusName(incoming_number) }}
                                                </b>
                                            </small>
                                        </el-option>
                                    </el-select>
                                </el-form-item>
                            </div>

                            <!-- Use Case Information Section -->
                            <div class="col-12 mb-3 mt-4">
                                <span class="text-dark h6">
                                    <i class="material-icons mr-2">description</i>Use Case Information
                                </span>
                            </div>

                            <div class="col-12 mt-1">
                                <!-- Use Case Categories -->
                                <el-form-item prop="use_case_categories" label="Messaging Use Case Categories">
                                    <span class="page-anchor" />
                                    <p class="line-height-normal">Choose the use case that best fits the types of messages being sent by this toll-free phone number.</p>

                                    <el-select
                                        v-model="tollFreeVerificationForm.use_case_categories"
                                        placeholder="Select Use Case Categories"
                                        class="w-full"
                                        multiple
                                        filterable
                                        :disabled="isEditableFieldDisabled"
                                        @change="handleFormFieldChange"
                                    >
                                        <el-option
                                            v-for="use_case in use_case_options"
                                            :key="use_case.value"
                                            :label="use_case.label"
                                            :value="use_case.value"
                                        />
                                    </el-select>
                                </el-form-item>
                            </div>

                            <div class="col-12 mt-3">
                                <!-- Use Case Summary -->
                                <el-form-item prop="use_case_summary" label="Use Case Summary">
                                    <span class="page-anchor" />
                                    <p class="line-height-normal">Describe what you will be using this toll-free number for including the purpose, audience and sender.</p>

                                    <el-input
                                        type="textarea"
                                        :rows="4"
                                        v-model="tollFreeVerificationForm.use_case_summary"
                                        placeholder="Provide a detailed description of how you plan to use this toll-free number for messaging..."
                                        maxlength="500"
                                        show-word-limit
                                        :disabled="isEditableFieldDisabled"
                                        @change="handleFormFieldChange"
                                    />
                                    <p class="line-height-normal mb-0"><b>Example</b>: <span class="font-italic">Provide assistance to our current customers, mainly conversational communication.</span></p>
                                </el-form-item>
                            </div>

                            <div class="col-12 mt-3">
                                <!-- Production Message Sample -->
                                <el-form-item prop="production_message_sample" label="Sample Message">
                                    <span class="page-anchor" />
                                    <p class="line-height-normal">Example text messages you plan to send on this toll-free phone number and what the consumer can expect to receive. The sample needs to represent the use case category chosen.</p>

                                    <el-input
                                        type="textarea"
                                        :rows="4"
                                        v-model="tollFreeVerificationForm.production_message_sample"
                                        placeholder="Provide a sample of the actual message content you plan to send..."
                                        maxlength="500"
                                        show-word-limit
                                        :disabled="isEditableFieldDisabled"
                                        @change="handleFormFieldChange"
                                    />
                                    <p class="line-height-normal mb-0"><b>Example</b>: <span class="font-italic">Hi {Customer Name}, this is {Agent Name} from {Company Name}. Thank you for reaching out. Reply with your question or call us at {Phone Number}. For account updates, visit {Company Website}. Reply STOP to opt-out.</span></p>
                                </el-form-item>
                            </div>

                            <div class="col-6 mt-3">
                                <!-- Message Volume -->
                                <el-form-item prop="message_volume" label="Estimated Monthly Message Volume">
                                    <span class="page-anchor" />
                                    <p class="line-height-normal">Select your estimated monthly message volume for this toll-free number.</p>

                                    <el-select
                                        v-model="tollFreeVerificationForm.message_volume"
                                        placeholder="Select estimated monthly volume"
                                        class="w-full"
                                        filterable
                                        :disabled="isEditableFieldDisabled"
                                        @change="handleFormFieldChange"
                                    >
                                        <el-option
                                            v-for="volume in volume_options"
                                            :key="volume.value"
                                            :label="volume.label"
                                            :value="volume.value"
                                        />
                                    </el-select>
                                </el-form-item>
                            </div>

                            <div class="col-6 mt-3">
                                <!-- Opt-in Type -->
                                <el-form-item prop="opt_in_type" label="Opt-in Type">
                                    <span class="page-anchor" />
                                    <p class="line-height-normal">How will consumers be opting into SMS programs on this toll-free number?</p>

                                    <el-select
                                        v-model="tollFreeVerificationForm.opt_in_type"
                                        placeholder="Select Opt-in Type"
                                        class="w-full"
                                        filterable
                                        :disabled="isEditableFieldDisabled"
                                        @change="handleFormFieldChange"
                                    >
                                        <el-option
                                            v-for="opt_in_type in opt_in_type_options"
                                            :key="opt_in_type.value"
                                            :label="opt_in_type.label"
                                            :value="opt_in_type.value"
                                        />
                                    </el-select>
                                </el-form-item>
                            </div>

                            <div class="col-12 mt-3">
                                <!-- Opt-in Image URLs -->
                                <el-form-item prop="opt_in_image_urls" label="Proof of consent (opt-in) collected">
                                    <span class="page-anchor" />
                                    <p class="line-height-normal">You must provide proof of consent to receive messaging collected from the consumer, which can be a link to a website where the consumer gives consent, a hosted image file that demonstrates a screenshot, or a link to a document that tells the story of the opt-in. Multiple URLs are allowed. Any URL submitted must be reachable, resolvable and of access to the public.</p>

                                    <el-input
                                        v-model="opt_in_urls_text"
                                        type="textarea"
                                        :rows="3"
                                        placeholder="Enter URLs (one per line) showing proof of consent..."
                                        @input="handleOptInUrlsChange"
                                        :disabled="isEditableFieldDisabled"
                                    />
                                    <small class="text-muted">Enter one URL per line.</small>
                                </el-form-item>
                            </div>

                            <div class="col-12 mt-3">
                                <!-- Additional Information -->
                                <el-form-item prop="additional_information" label="Additional Information">
                                    <span class="page-anchor" />
                                    <p class="line-height-normal">Any additional details that clarify your business use of the toll-free phone number for messaging. Links to privacy policies, AUPs, additional onboarding controls, etc, are good to include here.</p>

                                    <el-input
                                        type="textarea"
                                        :rows="4"
                                        v-model="tollFreeVerificationForm.additional_information"
                                        placeholder="Provide any additional information that might be helpful for the review process..."
                                        maxlength="1000"
                                        show-word-limit
                                        :disabled="isEditableFieldDisabled"
                                        @change="handleFormFieldChange"
                                    />
                                </el-form-item>
                            </div>

                            <!-- Edit Reason (only shown when rejected) -->
                            <div class="col-12 mt-3" v-if="canEdit">
                                <el-form-item prop="edit_reason" label="Reason for Edit">
                                    <span class="page-anchor" />
                                    <p class="line-height-normal">Please provide a reason for resubmitting this toll-free verification. This is required when resubmitting a rejected verification.</p>

                                    <el-input
                                        type="textarea"
                                        :rows="3"
                                        v-model="tollFreeVerificationForm.edit_reason"
                                        placeholder="Explain why you are resubmitting this verification and what changes were made..."
                                        maxlength="255"
                                        show-word-limit
                                        :disabled="!canEdit"
                                        @input="handleFormFieldChange"
                                    />
                                </el-form-item>
                            </div>

                            <!-- Action Buttons -->
                            <div class="col-12 mt-3">
                                <div class="row">
                                    <div class="col-6">
                                        <el-button
                                            @click="handleClose"
                                            class="w-full"
                                            :disabled="isCancelButtonDisabled"
                                        >
                                            Cancel
                                        </el-button>
                                    </div>
                                    <div class="col-6">
                                        <el-button
                                            type="primary"
                                            class="w-full"
                                            @click="handleSubmit"
                                            :loading="creating_toll_free_verification"
                                            :disabled="isSubmitButtonDisabled"
                                        >
                                            Submit
                                        </el-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </el-form>
        </el-dialog>
    </div>
</template>

<script>
import {form_validation_mixin} from '../../../mixins'
import {
    USE_CASE_OPTIONS,
    OPT_IN_TYPE_OPTIONS,
    VOLUME_OPTIONS
} from '../../../constants/toll-free-verification'
import * as ComplianceBundleStatuses from '../../../constants/compliance-bundle-statuses'

export default {
    name: 'TollFreeVerificationDialog',
    props: {
        visible: {
            type: Boolean,
            required: true,
        },
        company: {
            type: Object,
            required: true,
        },
        customerProfiles: {
            type: Array,
            required: true,
        },
        verification: {
            type: Object,
            default: null,
        },
    },
    mixins: [form_validation_mixin],
    data() {
        return {
            creating_toll_free_verification: false,
            loading_incoming_numbers: false,
            opt_in_urls_text: '',
            incoming_numbers: [],
            customer_profile_info: null,
            tollFreeVerificationForm: {
                customer_profile_id: null,
                incoming_number_ids: [],
                use_case_categories: [],
                use_case_summary: '',
                production_message_sample: '',
                opt_in_image_urls: [],
                opt_in_type: '',
                message_volume: '',
                additional_information: '',
                business_name: '',
                website_url: '',
                edit_reason: '',
            },
            use_case_options: USE_CASE_OPTIONS,
            opt_in_type_options: OPT_IN_TYPE_OPTIONS,
            volume_options: VOLUME_OPTIONS,
            ComplianceBundleStatuses,
        }
    },
    computed: {
        website_url: {
            get() {
                return this.tollFreeVerificationForm.website_url
            },
            set(value) {
                this.tollFreeVerificationForm.website_url = this.transformUrl(value)
            },
        },
        readOnlyMode() {
            return !!this.verification
        },
        isViewMode() {
            return this.readOnlyMode && !this.canEdit
        },
        isCustomerProfileSelected() {
            return this.tollFreeVerificationForm.customer_profile_id
        },
        canEdit() {
            if (!this.verification) {
                return false
            }

            // Check if it's rejected, edit is allowed, and expiration date hasn't passed
            return this.verification.status === ComplianceBundleStatuses.STATUS_REJECTED && 
                   this.verification.edit_allowed && 
                   !this.isEditExpired
        },
        isEditExpired() {
            if (!this.verification || !this.verification.edit_expiration) {
                return false
            }
            
            // Check if the edit expiration date has passed
            const expirationDate = new Date(this.verification.edit_expiration)
            const now = new Date()
            return now > expirationDate
        },
        isEditableFieldDisabled() {
            return !this.isCustomerProfileSelected || (this.readOnlyMode && !this.canEdit)
        },
        isBasicFieldDisabled() {
            return this.readOnlyMode || !this.isCustomerProfileSelected
        },
        isCancelButtonDisabled() {
            return this.creating_toll_free_verification || this.isViewMode || !this.isCustomerProfileSelected
        },
        isSubmitButtonDisabled() {
            return (this.isViewMode && !this.canEdit) || !this.isFormValid || !this.isCustomerProfileSelected
        },
        // No need for a custom formatter - we'll use the existing filter
        sortedIncomingNumbers() {
            return [...this.incoming_numbers].sort((a, b) => {
                const aVerified = this.isNumberVerified(a)
                const bVerified = this.isNumberVerified(b)
                
                if (aVerified && !bVerified) return 1
                if (!aVerified && bVerified) return -1
                
                return b.id - a.id
            })
        },
        statusAlertType() {
            if (!this.verification) {
                return ''
            }
            
            switch (this.verification.status) {
                case ComplianceBundleStatuses.STATUS_IN_REVIEW:
                case ComplianceBundleStatuses.STATUS_PENDING:
                    return 'info'
                case ComplianceBundleStatuses.STATUS_REJECTED:
                    return 'error'
                case ComplianceBundleStatuses.STATUS_APPROVED:
                    return 'success'
                default:
                    return ''
            }
        },
        statusAlertTitle() {
            if (!this.verification) {
                return ''
            }
            
            switch (this.verification.status) {
                case ComplianceBundleStatuses.STATUS_IN_REVIEW:
                case ComplianceBundleStatuses.STATUS_PENDING:
                    return 'Verification In Review'
                case ComplianceBundleStatuses.STATUS_REJECTED:
                    return 'Verification Rejected'
                case ComplianceBundleStatuses.STATUS_APPROVED:
                    return 'Verification Approved'
                default:
                    return ''
            }
        },
        isFormValid() {
            // In view mode but not rejected, no validation needed
            if (this.isViewMode && !this.canEdit) {
                return true
            }
            
            // Use the existing validated property from the mixin
            let isValid = this.validated
            
            // Additional validation for edit reason when in rejected status
            if (this.canEdit && !this.tollFreeVerificationForm.edit_reason) {
                isValid = false
            }
            
            return isValid
        },
        formRules() {
            if (this.isViewMode && !this.canEdit) {
                return {}
            }
            
            return {
                customer_profile_id: [
                    {
                        required: true,
                        message: "Please select a customer profile.",
                        trigger: "change",
                    },
                ],
                incoming_number_ids: [
                    {
                        required: true,
                        type: 'array',
                        min: 1,
                        message: "Please select at least one toll-free number.",
                        trigger: "change",
                    },
                    {
                        validator: this.validateIncomingNumbers,
                        trigger: "change",
                    },
                ],
                use_case_categories: [
                    {
                        required: true,
                        type: 'array',
                        min: 1,
                        message: "Please select at least one use case category.",
                        trigger: "change",
                    },
                ],
                use_case_summary: [
                    {
                        required: true,
                        message: "Please provide a use case summary.",
                        trigger: "blur",
                    },
                    {
                        min: 50,
                        message: "Use case summary must be at least 50 characters.",
                        trigger: "blur",
                    },
                ],
                production_message_sample: [
                    {
                        required: true,
                        message: "Please provide a production message sample.",
                        trigger: "blur",
                    },
                    {
                        min: 20,
                        message: "Message sample must be at least 20 characters.",
                        trigger: "blur",
                    },
                ],
                opt_in_type: [
                    {
                        required: true,
                        message: "Please select an opt-in type.",
                        trigger: "change",
                    },
                ],
                message_volume: [
                    {
                        required: true,
                        message: "Please select estimated monthly message volume.",
                        trigger: "change",
                    },
                ],
                opt_in_image_urls: [
                    {
                        required: true,
                        type: 'array',
                        min: 1,
                        message: "Please provide at least one proof of consent URL.",
                        trigger: "change",
                    },
                ],
                additional_information: [
                    {
                        min: 10,
                        message: "Additional information must be at least 10 characters.",
                        trigger: "change",
                    },
                ],
                business_name: [
                    {
                        required: true,
                        message: "Please provide a business name.",
                        trigger: "change",
                    },
                ],
                website_url: [
                    {
                        required: true,
                        message: "Please provide a website URL.",
                        trigger: "blur",
                    },
                    {
                        validator: (rule, value, callback) => {
                            if (typeof value !== 'string' || !value.startsWith('https://')) {
                                callback(new Error('Website URL must start with https://'))
                            } else if (value === 'https://') {
                                callback(new Error('Website URL must include a domain after https://'))
                            } else if (!/^https:\/\/\S+\.\S+$/.test(value)) {
                                callback(new Error('Website URL must be a valid URL'))
                            } else {
                                callback()
                            }
                        },
                        trigger: 'blur',
                    },
                ],
                edit_reason: [
                    {
                        validator: this.validateEditReason,
                        trigger: "change",
                    }
                ],
            }
        }
    },
    methods: {
        validateEditReason(rule, value, callback) {
            if (this.canEdit) {
                if (!value) {
                    callback(new Error('Please provide a reason for resubmitting this verification.'))
                } else if (value.length < 10) {
                    callback(new Error('Edit reason must be at least 10 characters.'))
                } else {
                    callback()
                }
            } else {
                callback()
            }
        },
        validateIncomingNumbers(rule, values, callback) {
            if (this.readOnlyMode) {
                callback()
                return
            }
            
            if (values && values.length > 0) {
                for (const value of values) {
                    const selectedNumber = this.incoming_numbers.find(num => num.id === value)
                    if (selectedNumber && this.isNumberVerified(selectedNumber)) {
                        callback(new Error('One or more selected numbers already have a toll-free verification. Please select only unverified numbers.'))
                        return
                    }
                }
            }
            callback()
        },
        loadVerificationData(verification) {
            if (!verification) return
            
            this.tollFreeVerificationForm = {
                customer_profile_id: verification.customer_profile_id,
                incoming_number_ids: verification.incoming_number_id ? [verification.incoming_number_id] : [],
                use_case_categories: verification.use_case_categories || [],
                use_case_summary: verification.use_case_summary || '',
                production_message_sample: verification.production_message_sample || '',
                opt_in_image_urls: verification.opt_in_image_urls || [],
                opt_in_type: verification.opt_in_type || '',
                message_volume: verification.message_volume || '',
                additional_information: verification.additional_information || '',
                business_name: verification.business_name || '',
                website_url: verification.website_url || verification.business_website || '',
                edit_reason: '',
            }
            
            if (verification.opt_in_image_urls && verification.opt_in_image_urls.length > 0) {
                this.opt_in_urls_text = verification.opt_in_image_urls.join('\n')
            }
            
            this.getIncomingNumbers()
            this.getCustomerProfileBusinessInfo()
            
            this.$nextTick(() => {
                if (this.$refs.tollFreeVerificationForm) {
                    this.$refs.tollFreeVerificationForm.clearValidate()
                }
            })
        },
        isNumberVerified(number) {
            return !!number.toll_free_messaging_verification
        },
        getNumberStatus(number) {
            return this.isNumberVerified(number) 
                ? number.toll_free_messaging_verification.status 
                : 'UNVERIFIED'
        },
        getNumberStatusName(number) {
            return this.isNumberVerified(number)
                ? this.$options.filters.statusName(number.toll_free_messaging_verification.status)
                : 'Unverified'
        },
        getNumberLabel(number) {
            return `#${number.id} - ${this.$options.filters.fixPhone(number.phone_number)} | Line: #${number.campaign?.id} ${number.campaign?.name}`
        },
        handleClose() {
            // Skip validation in view mode
            if (this.isViewMode) {
                this.$emit('close')
                return
            }
            
            if (this.hasFormData()) {
                this.$confirm('Are you sure you want to close this dialog? Any unsaved changes will be lost.', 'Confirm Close', {
                    confirmButtonText: 'Yes, Close',
                    cancelButtonText: 'Cancel',
                    type: 'warning'
                }).then(() => {
                    this.resetForm()
                    this.$emit('close')
                }).catch(() => {})
            } else {
                this.$emit('close')
            }
        },
        handleBeforeClose(done) {
            // Skip validation in view mode
            if (this.isViewMode) {
                this.$emit('close')
                done()
                return
            }
            
            if (this.hasFormData()) {
                this.$confirm('Are you sure you want to close this dialog? Any unsaved changes will be lost.', 'Confirm Close', {
                    confirmButtonText: 'Yes, Close',
                    cancelButtonText: 'Cancel',
                    type: 'warning'
                }).then(() => {
                    this.resetForm()
                    this.$emit('close')
                    done()
                }).catch(() => {})
            } else {
                this.$emit('close')
                done()
            }
        },
        hasFormData() {
            const form = this.tollFreeVerificationForm

            return !!(
                form.customer_profile_id ||
                (form.incoming_number_ids && form.incoming_number_ids.length > 0) ||
                form.business_name ||
                form.website_url ||
                (form.use_case_categories && form.use_case_categories.length > 0) ||
                form.use_case_summary ||
                form.production_message_sample ||
                (form.opt_in_image_urls && form.opt_in_image_urls.length > 0) ||
                form.opt_in_type ||
                form.message_volume ||
                form.additional_information ||
                this.opt_in_urls_text.trim()
            )
        },
        resetForm() {
            this.tollFreeVerificationForm = {
                customer_profile_id: null,
                incoming_number_ids: [],
                use_case_categories: [],
                use_case_summary: '',
                production_message_sample: '',
                opt_in_image_urls: [],
                opt_in_type: '',
                message_volume: '',
                additional_information: '',
                business_name: '',
                website_url: '',
                edit_reason: '',
            }
            this.opt_in_urls_text = ''
            this.incoming_numbers = []
            this.customer_profile_info = null
        },
        handleCustomerProfileChange() {
            this.tollFreeVerificationForm.incoming_number_ids = []
            this.incoming_numbers = []
            
            // Only reset business info in create/edit mode, not view mode
            if (!this.isViewMode) {
                this.tollFreeVerificationForm.business_name = ''
                this.tollFreeVerificationForm.website_url = ''
                this.customer_profile_info = null
            }
            
            if (this.tollFreeVerificationForm.customer_profile_id) {
                this.getIncomingNumbers()
                this.getCustomerProfileBusinessInfo()
            }

            this.handleFormFieldChange()
        },
        handleFormFieldChange() {
            this.preValidateForm('tollFreeVerificationForm')
        },
        handleOptInUrlsChange() {
            this.convertUrlsTextToArray()
            this.handleFormFieldChange()
        },
        handleSubmit() {
            // In view mode but not rejected, just close
            if (this.isViewMode && !this.canEdit) {
                this.handleClose()
                return
            }
            
            if (this.readOnlyMode && !this.canEdit) {
                this.$message({
                    message: 'This verification cannot be edited.',
                    type: 'warning'
                })
                return
            }

            this.$refs.tollFreeVerificationForm.validate((valid) => {
                if (!valid) {
                    this.$message.error('Please fix the validation errors before submitting.')
                    return
                }
                
                if (this.readOnlyMode) {
                    this.resubmitTollFreeVerification()
                } else {
                    this.createTollFreeVerification()
                }
            })
        },
        convertUrlsTextToArray() {
            const urls = this.opt_in_urls_text
                .split('\n')
                .map(url => url.trim())
                .filter(url => url.length > 0)
            
            this.tollFreeVerificationForm.opt_in_image_urls = urls
        },
        syncUrlsArrayToText() {
            if (this.tollFreeVerificationForm.opt_in_image_urls && this.tollFreeVerificationForm.opt_in_image_urls.length > 0) {
                this.opt_in_urls_text = this.tollFreeVerificationForm.opt_in_image_urls.join('\n')
            } else {
                this.opt_in_urls_text = ''
            }
        },
        getCustomerProfileBusinessInfo() {
            if (!this.tollFreeVerificationForm.customer_profile_id) {
                return
            }

            axios
                .get(`/api/admin/compliance/${this.company.id}/customer-profiles/${this.tollFreeVerificationForm.customer_profile_id}/business-info`)
                .then((res) => {
                    const businessInfo = res.data
                    if (businessInfo) {
                        // Only overwrite business name and website if they're not already set
                        if (!this.tollFreeVerificationForm.business_name) {
                            this.tollFreeVerificationForm.business_name = businessInfo.business_name || ''
                        }
                        if (!this.tollFreeVerificationForm.website_url) {
                            this.tollFreeVerificationForm.website_url = businessInfo.website_url || ''
                        }
                        this.customer_profile_info = businessInfo.profile_info
                        
                        this.$nextTick(() => {
                            if (this.$refs.tollFreeVerificationForm) {
                                this.$refs.tollFreeVerificationForm.validateField('business_name')
                                this.$refs.tollFreeVerificationForm.validateField('website_url')
                                this.preValidateForm('tollFreeVerificationForm')
                            }
                        })
                    }
                })
                .catch((err) => {
                    console.warn('Business info not available:', err.response?.data?.message)
                })
        },
        getIncomingNumbers() {
            if (!this.tollFreeVerificationForm.customer_profile_id) {
                return
            }

            this.loading_incoming_numbers = true

            axios
                .get(`/api/admin/compliance/${this.company.id}/customer-profiles/${this.tollFreeVerificationForm.customer_profile_id}/incoming-numbers`, {
                    params: { is_tollfree: true }
                })
                .then((res) => {
                    this.incoming_numbers = res.data
                    
                    if (this.readOnlyMode && this.verification && this.verification.incoming_number) {
                        const foundNumber = this.incoming_numbers.find(num => num.id === this.verification.incoming_number_id)
                        if (!foundNumber) {
                            this.incoming_numbers.push(this.verification.incoming_number)
                        }
                    }
                })
                .catch((err) => {
                    this.$root.handleErrors(err.response)
                    this.incoming_numbers = []
                })
                .finally(() => {
                    this.loading_incoming_numbers = false
                })
        },
        createTollFreeVerification() {
            this.creating_toll_free_verification = true
            this.convertUrlsTextToArray()

            axios
                .post(`/api/admin/compliance/${this.company.id}/toll-free-verifications`, this.tollFreeVerificationForm)
                .then(() => {
                    this.$notify({
                        title: "Toll-Free Verification Creation",
                        message: "Toll-Free Verification request submitted successfully!",
                        type: "success",
                        showClose: true,
                    })
                    this.resetForm()
                    this.$emit('close')
                    this.$emit('verification-created')
                })
                .catch((err) => {
                    console.error('Toll-Free Verification Error:', err)
                    
                    // Extract error message for better display
                    let errorMessage = 'An error occurred while processing your request.'
                    
                    if (err.response && err.response.data) {
                        if (err.response.data.message) {
                            errorMessage = err.response.data.message
                        } else if (err.response.data.error) {
                            errorMessage = err.response.data.error
                        } else if (typeof err.response.data === 'string') {
                            errorMessage = err.response.data
                        }
                    } else if (err.message) {
                        errorMessage = err.message
                    }
                    
                    // Show the actual error message
                    this.$message.error(errorMessage)
                })
                .finally(() => {
                    this.creating_toll_free_verification = false
                })
        },
        resubmitTollFreeVerification() {
            if (!this.verification || !this.canEdit) {
                return
            }

            this.creating_toll_free_verification = true
            this.convertUrlsTextToArray()

            const resubmissionData = {
                business_name: this.tollFreeVerificationForm.business_name,
                business_website: this.tollFreeVerificationForm.website_url,
                use_case_categories: this.tollFreeVerificationForm.use_case_categories,
                use_case_summary: this.tollFreeVerificationForm.use_case_summary,
                production_message_sample: this.tollFreeVerificationForm.production_message_sample,
                opt_in_image_urls: this.tollFreeVerificationForm.opt_in_image_urls,
                opt_in_type: this.tollFreeVerificationForm.opt_in_type,
                message_volume: this.tollFreeVerificationForm.message_volume,
                additional_information: this.tollFreeVerificationForm.additional_information,
                edit_reason: this.tollFreeVerificationForm.edit_reason,
            }

            axios
                .put(`/api/admin/compliance/${this.company.id}/toll-free-verifications/${this.verification.id}/resubmit`, resubmissionData)
                .then(() => {
                    this.$notify({
                        title: 'Toll-Free Verification Resubmission',
                        message: 'Toll-Free Verification resubmitted successfully!',
                        type: 'success',
                        showClose: true,
                    })
                    this.resetForm()
                    this.$emit('close')
                    this.$emit('verification-updated')
                })
                .catch((err) => {
                    console.error('Toll-Free Verification Resubmission Error:', err)
                    
                    // Extract error message for better display
                    let errorMessage = 'An error occurred while resubmitting your request.'
                    
                    if (err.response && err.response.data) {
                        if (err.response.data.message) {
                            errorMessage = err.response.data.message
                        } else if (err.response.data.error) {
                            errorMessage = err.response.data.error
                        } else if (typeof err.response.data === 'string') {
                            errorMessage = err.response.data
                        }
                    } else if (err.message) {
                        errorMessage = err.message
                    }
                    
                    // Show the actual error message
                    this.$message.error(errorMessage)
                })
                .finally(() => {
                    this.creating_toll_free_verification = false
                })
        },
    },
    mounted() {
        if (this.verification) {
            this.loadVerificationData(this.verification)
        }
    },
    watch: {
        verification: {
            handler(newVal) {
                if (newVal) {
                    this.loadVerificationData(newVal)
                }
            },
            immediate: true,
        },
        'tollFreeVerificationForm.opt_in_image_urls': {
            handler(newUrls) {
                const textareaUrls = this.opt_in_urls_text
                    .split('\n')
                    .map(url => url.trim())
                    .filter(url => url.length > 0)

                const urlsMatch = JSON.stringify(newUrls || []) === JSON.stringify(textareaUrls)

                if (!urlsMatch && newUrls && newUrls.length > 0) {
                    this.syncUrlsArrayToText()
                }
            },
            deep: true,
        },
    },
}
</script>

<style scoped>
.form-group {
    margin-bottom: 20px;
}

.el-form-item {
    margin-bottom: 18px;
}

.line-height-normal {
    line-height: 1.5;
    margin-bottom: 15px;
    font-size: 14px;
    color: #606266;
    overflow-wrap: break-word;
    word-break: normal;
    white-space: normal;
}

.page-anchor {
    display: block;
    position: relative;
    top: -60px;
    visibility: hidden;
}

.el-select-dropdown__item.is-disabled {
    opacity: 0.6;
}
</style>
